import React, {useEffect, useState, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import Toast from "cogo-toast";
import PageHeader from "../components/PageHeader";
import MarkerPicker from "./MarkerPicker";
import {emptyScene, addRect, addCircle, addLine, addText, addMarker, moveMarker, updateMarker, updateShape, setBackground, removeElement} from "./sceneModel";
import {serializeScene} from "./serializeScene";
import {parseSceneFromSvg} from "./parseScene";
import {svgToBackgroundDataUri} from "./sanitizeSvg";
import {apiGetMap, apiPutMap, apiGetTurnstileList} from "./Actions";
import {SET_MAP_EDITOR_SCENE, RESET_MAP_EDITOR} from "../../../reducers/mapEditor";
import {clientToSvg} from "./coords";
import {MARKER, clampScale} from "./markerIcon";

const TOOLS = {SELECT: "select", RECT: "rect", CIRCLE: "circle", LINE: "line", TEXT: "text", MARKER: "marker"};
const RESIZE = 14; // velikost úchytu pro změnu velikosti (v souřadnicích viewBoxu)

// Adaptace Material barev z návrhu na Tailwind v1 projekt (tokeny neexistují → inline).
const C = {
    primary: "#004ac6",
    primaryContainer: "#2563eb",
    surface: "#faf8ff",
    surfaceContainer: "#ededf9",
    surfaceLow: "#f3f3fe",
    surfaceLowest: "#ffffff",
    surfaceHigh: "#e7e7f3",
    outline: "#737686",
    outlineVariant: "#c3c6d7",
    onSurface: "#191b23",
    onSurfaceVariant: "#434655",
    secondary: "#505f76",
    error: "#ba1a1a",
    shapeStroke: "#334155",
};
const GRID_BG = "radial-gradient(circle, #e1e2ed 1px, transparent 1px)";

// --- Inline SVG ikony (feather-style), bez CDN fontu ---
const PATHS = {
    select: {d: "M4 3l7 17 2.2-7.8L21 10 4 3z", fill: true},
    zone: {d: "M4 4h16v16H4z"},
    circle: {d: "M12 21a9 9 0 100-18 9 9 0 000 18z"},
    line: {d: "M5 12h14"},
    text: {d: "M4 7V4h16v3 M9 20h6 M12 4v16"},
    turnstile: {d: "M6 3h12v18H6z M15 12h.01"},
    upload: {d: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12"},
    undo: {d: "M9 14L4 9l5-5 M4 9h11a5 5 0 010 10h-1"},
    redo: {d: "M15 14l5-5-5-5 M20 9H9a5 5 0 000 10h1"},
    save: {d: "M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z M17 21v-8H7v8 M7 3v5h8"},
    zoomIn: {d: "M11 18a7 7 0 100-14 7 7 0 000 14z M21 21l-4.35-4.35 M11 8v6 M8 11h6"},
    zoomOut: {d: "M11 18a7 7 0 100-14 7 7 0 000 14z M21 21l-4.35-4.35 M8 11h6"},
    trash: {d: "M3 6h18 M8 6V4h8v2 M19 6l-1 14H6L5 6 M10 11v6 M14 11v6"},
};
const Icon = ({name, size = 20}) => {
    const p = PATHS[name];
    if (!p) return null;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24"
             fill={p.fill ? "currentColor" : "none"}
             stroke={p.fill ? "none" : "currentColor"} strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
            <path d={p.d}/>
        </svg>
    );
};

const Toggle = ({on, onClick}) => (
    <button type="button" onClick={onClick} aria-pressed={on}
            style={{
                width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer",
                background: on ? C.primaryContainer : C.outlineVariant, position: "relative",
                transition: "background .15s", flexShrink: 0,
            }}>
        <span style={{
            position: "absolute", top: 2, left: on ? 18 : 2, width: 16, height: 16,
            borderRadius: 999, background: "#fff", transition: "left .15s",
        }}/>
    </button>
);

const MapEditor = () => {
    const dispatch = useDispatch();
    const user = useSelector((s) => s.user);
    const subjectId = user?.activeAreal?.id;
    const persisted = useSelector((s) => s.mapEditor?.scene);

    const [scene, setScene] = useState(persisted || emptyScene());
    const [tool, setTool] = useState(TOOLS.SELECT);
    const [turnstiles, setTurnstiles] = useState([]);
    const [pickerAt, setPickerAt] = useState(null);
    const [lineStart, setLineStart] = useState(null); // první bod rozkreslené čáry
    const [selected, setSelected] = useState(null); // id vybraného prvku (Vlastnosti)
    const [showGrid, setShowGrid] = useState(true); // jen pomůcka editoru (neukládá se)
    const [zoom, setZoom] = useState(1);
    // drag = {id, kind:'marker'|'move'|'resize', ...} | null
    const [drag, setDrag] = useState(null);
    // historie pro undo/redo — zásobníky snapshotů scény
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const svgRef = useRef(null);
    const sceneRef = useRef(scene);
    const baseRef = useRef(null); // snapshot před tažením / editací pole (jeden záznam historie na gesto)

    useEffect(() => { sceneRef.current = scene; }, [scene]);

    useEffect(() => {
        if (!subjectId) return;
        // apiGetTurnstileList hits GET /api/v2/enigoo/{subjectId}/turnstile, the same
        // endpoint used by Turnstile.js. Backend GetTurnstileAllDto responds with
        // {turnstile: [...], unknownTurnstile: [...]} — list is nested under "turnstile".
        apiGetTurnstileList(subjectId, (d) => setTurnstiles(d?.turnstile || []), () => {});
        if (!persisted) {
            apiGetMap(subjectId, (d) => { if (d?.svg) setScene(parseSceneFromSvg(d.svg)); }, () => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subjectId]);

    useEffect(() => {
        dispatch({type: SET_MAP_EDITOR_SCENE, scene});
    }, [scene, dispatch]);

    // --- Historie ---
    const pushBase = (base) => { setUndoStack((u) => [...u, base]); setRedoStack([]); };
    // commit = diskrétní úprava (přidat/smazat/podklad) — jeden krok historie
    const commit = (producer) => setScene((prev) => {
        const next = producer(prev);
        if (next !== prev) pushBase(prev);
        return next;
    });
    const undo = () => setUndoStack((u) => {
        if (!u.length) return u;
        const prev = u[u.length - 1];
        setRedoStack((r) => [...r, sceneRef.current]);
        setScene(prev);
        return u.slice(0, -1);
    });
    const redo = () => setRedoStack((r) => {
        if (!r.length) return r;
        const next = r[r.length - 1];
        setUndoStack((u) => [...u, sceneRef.current]);
        setScene(next);
        return r.slice(0, -1);
    });

    const selectTool = (t) => { setTool(t); setLineStart(null); };

    const toSvgCoords = (e) => clientToSvg(svgRef.current.getBoundingClientRect(), scene.viewBox, e.clientX, e.clientY);

    const onCanvasClick = (e) => {
        if (tool === TOOLS.SELECT) {
            // klik do prázdna zruší výběr; klik na prvek nezruší (target != svg)
            if (e.target === svgRef.current) setSelected(null);
            return;
        }
        const p = toSvgCoords(e);
        if (tool === TOOLS.RECT) {
            commit((s) => addRect(s, {x: p.x, y: p.y, w: 120, h: 80}));
        } else if (tool === TOOLS.CIRCLE) {
            commit((s) => addCircle(s, {cx: p.x, cy: p.y, r: 40}));
        } else if (tool === TOOLS.TEXT) {
            const v = window.prompt("Text popisku:");
            if (v) commit((s) => addText(s, {x: p.x, y: p.y, value: v}));
        } else if (tool === TOOLS.LINE) {
            // dvojklik-styl: 1. klik = start, 2. klik = konec segmentu
            if (!lineStart) setLineStart(p);
            else { commit((s) => addLine(s, [[lineStart.x, lineStart.y], [p.x, p.y]])); setLineStart(null); }
        } else if (tool === TOOLS.MARKER) {
            setPickerAt(p);
        }
    };

    const onPickTurnstile = (t) => {
        commit((s) => addMarker(s, {turnstileId: t.id, x: pickerAt.x, y: pickerAt.y, label: t.description || ("#" + t.id)}));
        setPickerAt(null);
        selectTool(TOOLS.SELECT);
    };

    // Drag jde jen v nástroji Výběr. Pointer capture zajistí, že tažení funguje
    // i když kurzor opustí SVG (a uvolnění mimo plátno drag ukončí).
    const capture = (e) => { if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId); };

    const beginMarkerDrag = (id) => (e) => {
        if (tool !== TOOLS.SELECT) return;
        e.stopPropagation();
        setSelected(id);
        capture(e);
        baseRef.current = sceneRef.current;
        const {x, y} = toSvgCoords(e);
        const m = scene.markers.find((mm) => mm.id === id);
        setDrag({id, kind: "marker", offX: x - m.x, offY: y - m.y});
    };
    const beginShapeMove = (id) => (e) => {
        if (tool !== TOOLS.SELECT) return;
        e.stopPropagation();
        setSelected(id);
        capture(e);
        baseRef.current = sceneRef.current;
        const {x, y} = toSvgCoords(e);
        const sh = scene.shapes.find((s) => s.id === id);
        setDrag({id, kind: "move", startX: x, startY: y, orig: sh});
    };
    const beginShapeResize = (id) => (e) => {
        if (tool !== TOOLS.SELECT) return;
        e.stopPropagation();
        setSelected(id);
        capture(e);
        baseRef.current = sceneRef.current;
        setDrag({id, kind: "resize"});
    };
    const onCanvasPointerMove = (e) => {
        if (!drag) return;
        const {x, y} = toSvgCoords(e);
        // tažení = živá úprava bez zápisu do historie (jeden záznam se přidá na pointer-up)
        setScene((s) => {
            if (drag.kind === "marker") return moveMarker(s, drag.id, x - drag.offX, y - drag.offY);
            if (drag.kind === "move") {
                const dx = x - drag.startX, dy = y - drag.startY, o = drag.orig;
                let patch = {};
                if (o.type === "rect" || o.type === "text") patch = {x: o.x + dx, y: o.y + dy};
                else if (o.type === "circle") patch = {cx: o.cx + dx, cy: o.cy + dy};
                else if (o.type === "line") patch = {points: o.points.map(([px, py]) => [px + dx, py + dy])};
                return updateShape(s, drag.id, patch);
            }
            if (drag.kind === "resize") {
                const sh = s.shapes.find((ss) => ss.id === drag.id);
                if (!sh) return s;
                if (sh.type === "rect") return updateShape(s, drag.id, {w: Math.max(10, x - sh.x), h: Math.max(10, y - sh.y)});
                if (sh.type === "circle") return updateShape(s, drag.id, {r: Math.max(5, Math.round(Math.hypot(x - sh.cx, y - sh.cy)))});
                return s;
            }
            return s;
        });
    };
    const onCanvasPointerUp = () => {
        // gesto skončilo — pokud se scéna změnila, ulož výchozí snapshot do historie
        if (drag && baseRef.current && baseRef.current !== sceneRef.current) pushBase(baseRef.current);
        baseRef.current = null;
        setDrag(null);
    };

    const onDeleteSelected = (id) => {
        commit((s) => removeElement(s, id));
        setSelected((sel) => (sel === id ? null : sel));
    };

    // --- Editace pozice z panelu Vlastnosti ---
    const findEl = (s, id) => s.shapes.find((x) => x.id === id) || s.markers.find((m) => m.id === id);
    const posOf = (el) => {
        if (!el) return null;
        if (el.type === "circle") return {x: el.cx, y: el.cy};
        if (el.type === "line") return {x: el.points[0][0], y: el.points[0][1]};
        return {x: el.x, y: el.y}; // rect, text, marker
    };
    const setPos = (axis, raw) => {
        const n = Math.round(Number(raw));
        if (Number.isNaN(n)) return;
        setScene((s) => {
            const isMarker = s.markers.some((m) => m.id === selected);
            if (isMarker) {
                const m = s.markers.find((mm) => mm.id === selected);
                return moveMarker(s, selected, axis === "x" ? n : m.x, axis === "y" ? n : m.y);
            }
            const sh = s.shapes.find((x) => x.id === selected);
            if (!sh) return s;
            if (sh.type === "circle") return updateShape(s, selected, {[axis === "x" ? "cx" : "cy"]: n});
            if (sh.type === "line") {
                const pts = sh.points.map((p) => [...p]);
                pts[0][axis === "x" ? 0 : 1] = n;
                return updateShape(s, selected, {points: pts});
            }
            return updateShape(s, selected, {[axis]: n});
        });
    };
    const setMarkerScale = (raw) => {
        const s = clampScale(raw);
        setScene((sc) => updateMarker(sc, selected, {scale: s}));
    };
    const beginFieldEdit = () => { baseRef.current = sceneRef.current; };
    const endFieldEdit = () => {
        if (baseRef.current && baseRef.current !== sceneRef.current) pushBase(baseRef.current);
        baseRef.current = null;
    };

    const onUploadBg = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUri = svgToBackgroundDataUri(String(reader.result));
            if (!dataUri) { Toast.error("Neplatné nebo prázdné SVG."); return; }
            commit((s) => setBackground(s, dataUri));
        };
        reader.readAsText(file);
        e.target.value = ""; // umožní znovu nahrát stejný soubor
    };
    const onRemoveBg = () => commit((s) => setBackground(s, null));

    const onSave = () => {
        const svg = serializeScene(scene);
        apiPutMap(subjectId, svg,
            () => { Toast.success("Mapa uložena."); dispatch({type: RESET_MAP_EDITOR}); },
            () => Toast.error("Uložení selhalo. Scéna zůstává rozpracovaná."));
    };

    const zoomBy = (f) => setZoom((z) => Math.min(3, Math.max(0.25, Math.round(z * f * 100) / 100)));

    const dragProps = (id) => ({
        onPointerDown: beginShapeMove(id),
        onPointerMove: onCanvasPointerMove,
        onPointerUp: onCanvasPointerUp,
        onPointerCancel: onCanvasPointerUp,
        onDoubleClick: () => onDeleteSelected(id),
        style: {cursor: tool === TOOLS.SELECT ? "move" : "default"},
    });
    const resizeProps = (id) => ({
        onPointerDown: beginShapeResize(id),
        onPointerMove: onCanvasPointerMove,
        onPointerUp: onCanvasPointerUp,
        onPointerCancel: onCanvasPointerUp,
        style: {cursor: "nwse-resize"},
    });

    const renderShape = (sh) => {
        const sel = sh.id === selected;
        const stroke = sel ? C.primaryContainer : C.shapeStroke;
        const sw = sel ? 2.5 : 1.5;
        if (sh.type === "rect") {
            return (
                <g key={sh.id}>
                    {/* slabá výplň → tažení chytá celou plochu, ne jen 1px okraj */}
                    <rect x={sh.x} y={sh.y} width={sh.w} height={sh.h}
                          fill="rgba(51,65,85,0.06)" stroke={stroke} strokeWidth={sw} {...dragProps(sh.id)}/>
                    {tool === TOOLS.SELECT && (
                        <rect x={sh.x + sh.w - RESIZE / 2} y={sh.y + sh.h - RESIZE / 2}
                              width={RESIZE} height={RESIZE} fill={C.primaryContainer} {...resizeProps(sh.id)}/>
                    )}
                </g>
            );
        }
        if (sh.type === "circle") {
            return (
                <g key={sh.id}>
                    <circle cx={sh.cx} cy={sh.cy} r={sh.r}
                            fill="rgba(51,65,85,0.06)" stroke={stroke} strokeWidth={sw} {...dragProps(sh.id)}/>
                    {tool === TOOLS.SELECT && (
                        <rect x={sh.cx + sh.r - RESIZE / 2} y={sh.cy - RESIZE / 2}
                              width={RESIZE} height={RESIZE} fill={C.primaryContainer} {...resizeProps(sh.id)}/>
                    )}
                </g>
            );
        }
        if (sh.type === "line") {
            const pts = sh.points.map(([x, y]) => `${x},${y}`).join(" ");
            return (
                <g key={sh.id}>
                    {/* neviditelná tlustá čára = snadný cíl pro tažení */}
                    <polyline points={pts} fill="none" stroke="transparent" strokeWidth="14" {...dragProps(sh.id)}/>
                    <polyline points={pts} fill="none" stroke={stroke} strokeWidth={sel ? 3 : 2} style={{pointerEvents: "none"}}/>
                </g>
            );
        }
        if (sh.type === "text") {
            return (
                <text key={sh.id} x={sh.x} y={sh.y} fontSize="14" fill={sel ? C.primaryContainer : C.shapeStroke}
                      fontWeight={sel ? 700 : 400} {...dragProps(sh.id)}>
                    {sh.value}
                </text>
            );
        }
        return null;
    };

    const selEl = selected ? findEl(scene, selected) : null;
    const selPos = posOf(selEl);
    const selIsMarker = !!selEl && !selEl.type; // markery nemají pole `type`
    // Popisky se ukládají do scény (platí i pro prohlížeč); default = zobrazit.
    const showLabels = scene.labels !== false;
    const toggleLabels = () => commit((s) => (showLabels ? {...s, labels: false} : {...s, labels: undefined}));
    const selScale = selIsMarker ? (selEl.scale || MARKER.DEFAULT_SCALE) : MARKER.DEFAULT_SCALE;

    // --- Styly ---
    const shell = {
        display: "flex", flexDirection: "column", height: "calc(100vh - 190px)", minHeight: 480,
        border: `1px solid ${C.outlineVariant}`, borderRadius: 12, overflow: "hidden",
        background: C.surfaceLowest, color: C.onSurface,
    };
    const toolBtn = (active) => ({
        width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
        borderRadius: 12, border: "none", cursor: "pointer",
        background: active ? C.primaryContainer : "transparent",
        color: active ? "#fff" : C.onSurfaceVariant,
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.18)" : "none",
    });
    const divider = {width: 32, height: 1, background: C.outlineVariant};
    const actionBtn = (disabled) => ({
        display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34,
        borderRadius: 8, border: `1px solid ${C.outlineVariant}`, background: C.surfaceLowest,
        color: C.onSurfaceVariant, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1,
    });
    const sectionLabel = {
        fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: C.outline,
        fontWeight: 600, marginBottom: 8, display: "block",
    };
    const posInput = {
        background: C.surfaceLow, border: `1px solid ${C.outlineVariant}`, borderRadius: 6,
        padding: "5px 8px", fontSize: 14, width: "100%", color: C.onSurface,
    };

    const tools = [
        {group: [{t: TOOLS.SELECT, icon: "select", title: "Výběr"}]},
        {group: [
            {t: TOOLS.RECT, icon: "zone", title: "Zóna"},
            {t: TOOLS.CIRCLE, icon: "circle", title: "Kruh"},
            {t: TOOLS.LINE, icon: "line", title: "Čára"},
        ]},
        {group: [
            {t: TOOLS.TEXT, icon: "text", title: "Text"},
            {t: TOOLS.MARKER, icon: "turnstile", title: "Turniket"},
        ]},
    ];

    return (
        <div>
            <PageHeader crumbs={[{label: "Mapa turniketů", to: "/turnstile-map"}, {label: "Editor mapy"}]}>
                <button type="button" onClick={undo} disabled={!undoStack.length}
                        title="Zpět" style={actionBtn(!undoStack.length)}>
                    <Icon name="undo" size={18}/>
                </button>
                <button type="button" onClick={redo} disabled={!redoStack.length}
                        title="Vpřed" style={actionBtn(!redoStack.length)}>
                    <Icon name="redo" size={18}/>
                </button>
                <button type="button" onClick={onSave} style={{
                    display: "flex", alignItems: "center", gap: 6, background: C.primary, color: "#fff",
                    border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                    cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}>
                    <Icon name="save" size={18}/> Uložit změny
                </button>
            </PageHeader>

            <div style={shell}>
                <div style={{display: "flex", flex: 1, minHeight: 0}}>
                    {/* Levá lišta nástrojů */}
                    <aside style={{
                        width: 64, background: C.surfaceContainer, borderRight: `1px solid ${C.outlineVariant}`,
                        display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 10,
                    }}>
                        {tools.map((section, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <div style={divider}/>}
                                <div style={{display: "flex", flexDirection: "column", gap: 8}}>
                                    {section.group.map((b) => (
                                        <button key={b.t} type="button" title={b.title}
                                                onClick={() => selectTool(b.t)} style={toolBtn(tool === b.t)}>
                                            <Icon name={b.icon}/>
                                        </button>
                                    ))}
                                </div>
                            </React.Fragment>
                        ))}
                        <div style={{marginTop: "auto"}}>
                            <label title="Nahrát SVG podklad" style={{...toolBtn(false), cursor: "pointer"}}>
                                <Icon name="upload"/>
                                <input type="file" accept=".svg,image/svg+xml" onChange={onUploadBg} style={{display: "none"}}/>
                            </label>
                        </div>
                    </aside>

                    {/* Plátno */}
                    <div style={{position: "relative", flex: 1, minWidth: 0, background: C.surfaceLowest}}>
                        <div style={{
                            position: "absolute", inset: 0, overflow: "auto",
                            backgroundImage: showGrid ? GRID_BG : "none", backgroundSize: "24px 24px",
                        }}>
                            <div style={{transform: `scale(${zoom})`, transformOrigin: "0 0", width: "100%", height: "100%"}}>
                                <svg
                                    ref={svgRef}
                                    viewBox={`0 0 ${scene.viewBox.w} ${scene.viewBox.h}`}
                                    style={{width: "100%", height: "100%", display: "block", background: "transparent"}}
                                    onClick={onCanvasClick}
                                    onPointerMove={onCanvasPointerMove}
                                    onPointerUp={onCanvasPointerUp}
                                    onPointerCancel={onCanvasPointerUp}
                                    onPointerLeave={onCanvasPointerUp}>
                                    {scene.background && (
                                        <image href={scene.background} x="0" y="0"
                                               width={scene.viewBox.w} height={scene.viewBox.h}
                                               preserveAspectRatio="xMidYMid meet" opacity="0.6"
                                               style={{pointerEvents: "none"}}/>
                                    )}
                                    <g className="layer-shapes">
                                        {scene.shapes.map(renderShape)}
                                    </g>
                                    <g className="layer-markers">
                                        {scene.markers.map((m) => {
                                            const sel = m.id === selected;
                                            const sc = m.scale || MARKER.DEFAULT_SCALE;
                                            return (
                                                <g key={m.id} transform={`translate(${m.x},${m.y}) scale(${sc})`}
                                                   style={{cursor: "move"}}
                                                   onPointerDown={beginMarkerDrag(m.id)}
                                                   onPointerMove={onCanvasPointerMove}
                                                   onPointerUp={onCanvasPointerUp}
                                                   onPointerCancel={onCanvasPointerUp}
                                                   onDoubleClick={() => onDeleteSelected(m.id)}>
                                                    {sel && <rect x={MARKER.SELECT.x} y={MARKER.SELECT.y} width={MARKER.SELECT.w} height={MARKER.SELECT.h} rx={MARKER.SELECT.rx} fill="none" stroke={C.primary} strokeWidth="2"/>}
                                                    {/* tělo turniketu — musí zrcadlit markerBodySvg() v markerIcon.js */}
                                                    <rect x="-19" y="-16" width="40" height="40" rx="12" fill="rgba(0,0,0,0.18)"/>
                                                    <rect x="-20" y="-20" width="40" height="40" rx="12" fill={MARKER.fill} stroke={MARKER.stroke} strokeWidth="1.5"/>
                                                    <path d={MARKER.glyphPath} fill="none" stroke={MARKER.arm} strokeWidth="3" strokeLinecap="round"/>
                                                    <circle cx="0" cy="0" r="2.4" fill={MARKER.arm}/>
                                                    {showLabels && (
                                                        <text y={MARKER.LABEL_DY} fontSize="12" textAnchor="middle" fill={C.onSurface}>{m.label}</text>
                                                    )}
                                                </g>
                                            );
                                        })}
                                    </g>
                                    {lineStart && (
                                        <circle cx={lineStart.x} cy={lineStart.y} r="4" fill="#f59e0b" style={{pointerEvents: "none"}}/>
                                    )}
                                </svg>
                            </div>
                        </div>

                        {/* Ovládání zoomu */}
                        <div style={{
                            position: "absolute", right: 16, bottom: 16, display: "flex", flexDirection: "column",
                            background: "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)",
                            border: `1px solid ${C.outlineVariant}`, borderRadius: 12, overflow: "hidden",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        }}>
                            <button type="button" title="Přiblížit" onClick={() => zoomBy(1.2)}
                                    style={{padding: 8, border: "none", borderBottom: `1px solid ${C.outlineVariant}`, background: "transparent", color: C.onSurfaceVariant, cursor: "pointer"}}>
                                <Icon name="zoomIn"/>
                            </button>
                            <div style={{padding: "4px 8px", textAlign: "center", fontSize: 12, background: C.surfaceLow, fontFamily: "monospace"}}>
                                {Math.round(zoom * 100)}%
                            </div>
                            <button type="button" title="Oddálit" onClick={() => zoomBy(1 / 1.2)}
                                    style={{padding: 8, border: "none", background: "transparent", color: C.onSurfaceVariant, cursor: "pointer"}}>
                                <Icon name="zoomOut"/>
                            </button>
                        </div>
                    </div>

                    {/* Pravý panel Vlastnosti */}
                    <aside style={{
                        width: 288, background: C.surface, borderLeft: `1px solid ${C.outlineVariant}`,
                        display: "flex", flexDirection: "column",
                    }}>
                        <div style={{padding: 16, borderBottom: `1px solid ${C.outlineVariant}`}}>
                            <h3 style={{fontSize: 16, fontWeight: 600, margin: 0}}>Vlastnosti</h3>
                        </div>
                        <div style={{flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 24}}>
                            <section>
                                <label style={sectionLabel}>Pozice</label>
                                {selPos ? (
                                    <>
                                        <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8}}>
                                            <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                                                <span style={{fontSize: 10, color: C.onSurfaceVariant}}>X (px)</span>
                                                <input type="number" style={posInput} value={Math.round(selPos.x)}
                                                       onFocus={beginFieldEdit} onBlur={endFieldEdit}
                                                       onChange={(e) => setPos("x", e.target.value)}/>
                                            </div>
                                            <div style={{display: "flex", flexDirection: "column", gap: 4}}>
                                                <span style={{fontSize: 10, color: C.onSurfaceVariant}}>Y (px)</span>
                                                <input type="number" style={posInput} value={Math.round(selPos.y)}
                                                       onFocus={beginFieldEdit} onBlur={endFieldEdit}
                                                       onChange={(e) => setPos("y", e.target.value)}/>
                                            </div>
                                        </div>
                                        {selIsMarker && (
                                            <div style={{marginTop: 14}}>
                                                <span style={{fontSize: 10, color: C.onSurfaceVariant, display: "flex", justifyContent: "space-between"}}>
                                                    <span>Velikost</span>
                                                    <span style={{fontFamily: "monospace"}}>{Math.round(selScale * 100)}%</span>
                                                </span>
                                                <input type="range" min={MARKER.MIN_SCALE} max={MARKER.MAX_SCALE} step="0.1"
                                                       value={selScale} style={{width: "100%", accentColor: C.primary}}
                                                       onMouseDown={beginFieldEdit} onMouseUp={endFieldEdit}
                                                       onKeyDown={beginFieldEdit} onBlur={endFieldEdit}
                                                       onChange={(e) => setMarkerScale(e.target.value)}/>
                                            </div>
                                        )}
                                        <button type="button" onClick={() => onDeleteSelected(selected)}
                                                style={{
                                                    marginTop: 12, display: "flex", alignItems: "center", gap: 6,
                                                    background: "transparent", border: `1px solid ${C.error}`, color: C.error,
                                                    borderRadius: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer",
                                                }}>
                                            <Icon name="trash" size={16}/> Smazat prvek
                                        </button>
                                    </>
                                ) : (
                                    <p style={{fontSize: 13, color: C.onSurfaceVariant, margin: 0}}>
                                        Nic není vybráno. Ve „Výběru" klikni na prvek.
                                    </p>
                                )}
                            </section>

                            <section>
                                <label style={sectionLabel}>Zobrazení</label>
                                <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                        <span style={{fontSize: 14}}>Mřížka</span>
                                        <Toggle on={showGrid} onClick={() => setShowGrid((v) => !v)}/>
                                    </div>
                                    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                        <span style={{fontSize: 14}}>Popisky</span>
                                        <Toggle on={showLabels} onClick={toggleLabels}/>
                                    </div>
                                </div>
                            </section>

                            {scene.background && (
                                <section>
                                    <label style={sectionLabel}>Podklad</label>
                                    <button type="button" onClick={onRemoveBg}
                                            style={{background: "transparent", border: `1px solid ${C.outlineVariant}`, color: C.error, borderRadius: 8, padding: "6px 10px", fontSize: 13, cursor: "pointer"}}>
                                        Odebrat podklad
                                    </button>
                                </section>
                            )}
                        </div>
                        <div style={{padding: 16, borderTop: `1px solid ${C.outlineVariant}`, background: C.surfaceLow}}>
                            <div style={{display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.onSurfaceVariant}}>
                                <span style={{width: 8, height: 8, borderRadius: 999, background: "#22c55e"}}/>
                                Systém Online
                            </div>
                        </div>
                    </aside>
                </div>

                {/* Patička s nápovědou */}
                <footer style={{
                    background: C.surfaceHigh, borderTop: `1px solid ${C.outlineVariant}`,
                    padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: 12, color: C.onSurfaceVariant,
                }}>
                    <span>
                        <strong>Nápověda:</strong> Zóna/Kruh + klik = vytvořit. Text + klik = popisek.
                        Čára = klik start/konec. Turniket + klik = značka. Ve „Výběru" táhni = přesun, roh = velikost. Dvojklik = smazat.
                    </span>
                    <span style={{fontFamily: "monospace", color: C.outline}}>editor mapy</span>
                </footer>
            </div>

            {pickerAt && (
                <MarkerPicker turnstiles={turnstiles} onPick={onPickTurnstile} onClose={() => setPickerAt(null)}/>
            )}
        </div>
    );
};

export default MapEditor;
